import type { IconName } from '@fortawesome/fontawesome-common-types';
import { Link, Typography } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ConfigOptionList from '@components/ConfigOptionList/ConfigOptionList';

import { MfaMethod } from 'generated/types';

interface MfaMethodListProps {
  onClick: (value: MfaMethod) => void;
  method: string;
  config: {
    showConfig: boolean;
    smsConfigured?: boolean;
    totpConfigured?: boolean;
    emailConfigured?: boolean;
    totpDisabled?: boolean;
    smsDisabled?: boolean;
    emailDisabled?: boolean;
  };
  extraContent?: Partial<Record<MfaMethod, React.ReactNode>>;
}

const messages = defineMessages({
  methods: {
    id: 'changeMfa.methods',
    defaultMessage: 'MFA Methods',
  },
  codeRequired: {
    id: 'changeMfa.codeRequired',
    defaultMessage: 'Authentication code is required',
  },
  error: {
    id: 'changeMfa.error',
    defaultMessage:
      '{text} Please try to {configure} MFA again with a valid code.',
  },
  configured: {
    id: 'changeMfa.configured',
    defaultMessage: 'Configured',
  },
  notConfigured: {
    id: 'changeMfa.notConfigured',
    defaultMessage: 'Not Configured',
  },
  disableMfa_title: {
    defaultMessage: 'None',
    id: 'changeMfa.disable.title',
  },
  smsMfa_title: {
    defaultMessage: 'SMS',
    id: 'changeMfa.sms.title',
  },
  totpMfa_title: {
    defaultMessage: 'Authenticator App',
    id: 'changeMfa.totp.title',
  },
  disableMfa_subtitle: {
    defaultMessage: 'Least secure option',
    id: 'changeMfa.disable.subtitle',
  },
  smsMfa_subtitle: {
    defaultMessage:
      'Receive a one-time sign-in code by text message each time you log in. Requires cell service.',
    id: 'changeMfa.sms.subtitle',
  },
  totpMfa_subtitle: {
    defaultMessage:
      'Get a secure code from a free app on your phone each time you sign in. Works without cell service.',
    id: 'changeMfa.totp.subtitle',
  },
  totpMfa_try: {
    defaultMessage: 'Try:',
    id: 'changeMfa.totp.try',
  },
  emailMfa_title: {
    defaultMessage: 'Email',
    id: 'changeMfa.email.title',
  },
  emailMfa_subtitle: {
    defaultMessage:
      "We'll send a one-time code to your email address each time you sign in.",
    id: 'changeMfa.email.subtitle',
  },
  mostSecure: {
    defaultMessage: 'Most Secure',
    id: 'changeMfa.security.mostSecure',
  },
  lessSecure: {
    defaultMessage: 'Less Secure',
    id: 'changeMfa.security.lessSecure',
  },
});

export default function MfaMethodList({
  onClick,
  config: {
    showConfig,
    smsConfigured,
    totpConfigured,
    emailConfigured,
    smsDisabled,
    totpDisabled,
    emailDisabled,
  },
  extraContent,
  method: selectedMethod,
}: MfaMethodListProps) {
  const { formatMessage } = useIntl();

  const authenticatorApps = [
    {
      name: 'Google Authenticator',
      url: 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2',
    },
    {
      name: 'Microsoft Authenticator',
      url: 'https://www.microsoft.com/en-us/security/mobile-authenticator-app',
    },
    {
      name: 'Authy',
      url: 'https://authy.com/',
    },
  ];

  const totpSubheader = (
    <>
      {formatMessage(messages.totpMfa_subtitle)}
      <Typography variant="caption" component="span" display="block" mt={1}>
        {formatMessage(messages.totpMfa_try)}{' '}
        {authenticatorApps.map((app, i) => (
          <React.Fragment key={app.name}>
            <Link
              href={app.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              underline="always"
              color="inherit"
              sx={{ fontSize: 'inherit' }}
            >
              {app.name}
            </Link>
            {i < authenticatorApps.length - 1 && ', '}
          </React.Fragment>
        ))}
      </Typography>
    </>
  );

  const MfaMethodMap: Record<
    MfaMethod,
    { display: string; subheader: React.ReactNode; isConfigured: boolean }
  > = {
    [MfaMethod.NONE]: {
      display: formatMessage(messages.disableMfa_title),
      subheader: formatMessage(messages.disableMfa_subtitle),
      isConfigured: false,
    },
    [MfaMethod.SMS_MFA]: {
      display: formatMessage(messages.smsMfa_title),
      subheader: formatMessage(messages.smsMfa_subtitle),
      isConfigured: smsConfigured ?? false,
    },
    [MfaMethod.SOFTWARE_TOKEN_MFA]: {
      display: formatMessage(messages.totpMfa_title),
      subheader: totpSubheader,
      isConfigured: totpConfigured ?? false,
    },
    [MfaMethod.EMAIL_OTP]: {
      display: formatMessage(messages.emailMfa_title),
      subheader: formatMessage(messages.emailMfa_subtitle),
      isConfigured: emailConfigured ?? false,
    },
  };

  const iconMap: Record<string, IconName> = {
    [MfaMethod.SOFTWARE_TOKEN_MFA]: 'mobile',
    [MfaMethod.SMS_MFA]: 'message',
    [MfaMethod.EMAIL_OTP]: 'envelope',
  };

  const mfaMethods = React.useMemo(() => {
    const methods: MfaMethod[] = [];
    if (!totpDisabled) {
      methods.push(MfaMethod.SOFTWARE_TOKEN_MFA);
    }
    if (!smsDisabled) {
      methods.push(MfaMethod.SMS_MFA);
    }
    if (!emailDisabled) {
      methods.push(MfaMethod.EMAIL_OTP);
    }
    return methods;
  }, [totpDisabled, smsDisabled, emailDisabled]);

  const securityTagMap: Partial<
    Record<MfaMethod, { label: string; variant: 'success' | 'error' }>
  > = {
    [MfaMethod.SOFTWARE_TOKEN_MFA]: {
      label: formatMessage(messages.mostSecure),
      variant: 'success',
    },
    [MfaMethod.SMS_MFA]: {
      label: formatMessage(messages.lessSecure),
      variant: 'error',
    },
    [MfaMethod.EMAIL_OTP]: {
      label: formatMessage(messages.lessSecure),
      variant: 'error',
    },
  };

  const items = mfaMethods.map((method) => {
    const isConfigured = MfaMethodMap[method].isConfigured;
    return {
      key: method,
      handleClick: () => {
        onClick(method);
      },
      iconName: iconMap[method],
      isConfigured,
      isSelected: selectedMethod === method,
      showConfigTag: showConfig,
      title: MfaMethodMap[method].display,
      subtitle: MfaMethodMap[method].subheader,
      securityTag: securityTagMap[method],
      extraContent: extraContent?.[method],
    };
  });

  return <ConfigOptionList items={items} />;
}
